<!--
 * A Vue data mixins that wraps the functionality of the b-modal element of Bootstrap Vue.
 *
 * Method call
 * ==========
 * msgBoxConfirm
 *  Call this method from your component and subscribe to the 'then' part.
 *  Clicking OK, return true and clicking Cancel return false.
 *
 * Properties
 * ==========
 * - message -
 * (Optional) Message to show on the modal dialog.
 *
 * - title -
 * (Optional) Sets the title of the modal dialog.
 *
 * - size -
 * (Optional) Sets the size of the modal dialog.
 *
 * - buttonSize -
 * (Optional) Sets the size of the buttons on modal dialog.
 *
 * - okVariant -
 * (Optional) Sets the color of the OK button.
 *
 * - okTitle -
 * (Optional) Sets the text of the OK button.
 *
 * - cancelVariant -
 * (Optional) Sets the color of the Cancel button.
 *
 * - cancelTitle -
 * (Optional) Sets the text of the Cancel button.
 *
 * Usage
 * ==========
 *   import modalMixin from '../commoncomponents/Mixins/ModalMixin.vue'
 *   export default {
 *       mixins: [modalMixin],
 *       ....
 *       ....
 *       ....
 *
 * let data = {
 *     message: 'Your message',
 *     ....,
 *     ....
 *   }
 *   this.msgBoxConfirm(data).then(result => {
 *       // put your logic here
 *   });
 *
 *
-->

<script>
    export default {
        data() {
            return { defaultMessage: 'Er is een onbekende fout opgetreden' };
        },

        mounted() {
            this.$root.$on('bv::toast:show', () => {
                this.$store.dispatch('tempHideAlert', true);
            });
            this.$root.$on('bv::toast:hidden', () => {
                this.$store.dispatch('tempHideAlert', false);
            });
        },

        methods: {
            msgBoxConfirm(input) {
                const data = input || {};
                const message = data.message || this.defaultMessage;
                return new Promise((resolve, reject) => {
                    this.$bvModal
                        .msgBoxConfirm(message, {
                            title: data.title ? data.title : '',
                            size: data.size ? data.size : 'md',
                            buttonSize: data.buttonSize ? data.buttonSize : 'md',
                            okVariant: data.okVariant ? data.okVariant : 'primary',
                            okTitle: data.okTitle
                                ? data.okTitle
                                : this.$t('btnTitleYes'),
                            cancelVariant: data.cancelVariant
                                ? data.cancelVariant
                                : 'light',
                            cancelTitle: data.cancelTitle
                                ? data.cancelTitle
                                : this.$t('btnCancelLabel'),
                            footerClass: 'p-2 switch_buttons',
                            hideHeaderClose: false,
                            centered: true,
                            noCloseOnBackdrop: data.noCloseOnBackdrop
                                ? data.noCloseOnBackdrop
                                : false
                        })
                        .then((value) => {
                            resolve(value);
                        })
                        .catch((error) => {
                            reject(error);
                        });
                });
            },

            /**
             * show a OK messagebox
             * @param  {object} input, object with option fields
             *
             * Method call
             * ==========
             * msgBoxOk
             *  Call this method from your component.
             *
             * Properties
             * ==========
             * - message -
             * (Optional) Message to show on the modal dialog.
             *
             * Usage
             * ==========
             *   import modalMixin from '../commoncomponents/Mixins/ModalMixin.vue'
             *   export default {
             *       mixins: [modalMixin],
             *       ....
             *       ....
             *       ....
             *
             *   this.msgBoxOk({ message: 'Your message' });
             *
             */
            msgBoxOk(input) {
                const data = input || {};
                const message = data.message || this.defaultMessage;
                const title = data.title || '';
                return new Promise((resolve, reject) => {
                    this.$bvModal
                        .msgBoxOk(message, {
                            title: title,
                            size: 'md',
                            centered: true
                        })
                        .then((value) => {
                            resolve(value);
                        })
                        .catch((error) => {
                            reject(error);
                        });
                });
            },

            /**
             * show a toast message
             * @param  {object} input, object with option fields
             *
             * Method call
             * ==========
             * showToast
             *  Call this method from your component.
             *
             * Properties
             * ==========
             * - message -
             * (Optional) Message to show on the modal dialog.
             *
             * - title -
             * (Optional) Sets the title of the modal dialog.
             *
             * - autoHideDelay -
             * (Optional) Autohide time in ms, default is 5000.
             *
             * - variant -
             * (Optional) Sets the variant type of the toast.
             * Variant types:
             *     primary
             *     secondary
             *     danger
             *     warning
             *     success
             *     info
             *
             * - noAutoHide -
             * (Optional) Set to true to disable auto hide .
             *
             * - noCloseButton -
             * (Optional) Set to true to remove the close icon on the title area.
             * When noCloseButton is set to true, a close button will be generated and placed into the message area.
             *
             * Usage
             * ==========
             *   import modalMixin from '../commoncomponents/Mixins/ModalMixin.vue'
             *   export default {
             *       mixins: [modalMixin],
             *       ....
             *       ....
             *       ....
             *
             * let data = {
             *     message: 'Your message',
             *     ....,
             *     ....
             *   }
             *   this.showToast(data);
             *
             */
            showToast(input) {
                const data = input || {};
                const element = this.$createElement;
                if (element) {
                    const toastIcon = this.$_getToastIcon(element, data.variant);
                    const closeButton =
                        data.noCloseButton === true || !data.title
                            ? this.$_createCloseBtn()
                            : null;
                    const message = [
                        toastIcon,
                        this.$_createMessage(data.message || this.defaultMessage),
                        closeButton
                    ];
                    this.$root.$bvToast.toast(message, {
                        title: data.title,
                        variant: data.variant || 'info',
                        autoHideDelay: data.autoHideDelay || 5000,
                        noAutoHide: data.noAutoHide || false,
                        solid: true,
                        appendToast: false,
                        noCloseButton: data.noCloseButton || !data.title || false,
                        toaster: 'b-toaster-top-center'
                    });
                }
            },

            hideToast() {
                this.$root.$bvToast.hide();
            },

            /*
             * create a message, used when closing a domain-modal
             * @param  {object} domain
             * @returns {string} the message to show in a toast
             */
            getDomainModalMessage(domain) {
                let message = '';
                if (domain.keyvalues.length === 0) {
                    // create message
                    message = this.$t('domainEmptyMessage');
                    if (domain.isGenerated) {
                        if (domain.isGenerating) {
                            message += this.$t('domainEmptyMessageBusy');
                        } else {
                            message += this.$t('domainEmptyMessageScheduled');
                        }
                    }
                }
                return message;
            },

            /*** Private functions for toast component ***/
            /**
             * Returns appropriate icon based on choosen variant
             * @param variant
             */
            $_getToastIcon(element, variant) {
                if (element) {
                    switch (variant) {
                        case 'success':
                            return element('em', { class: 'fas fa-check' });
                        case 'warning':
                            return element('em', {
                                class: 'fas fa-exclamation-triangle'
                            });
                        case 'danger':
                            return element('em', {
                                class: 'fas fa-exclamation-circle'
                            });
                        default:
                            return element('em', { class: 'fas fa-info-circle' });
                    }
                }
                return null;
            },

            /*
             * Hides the current toast element
             */
            $_clickHandler() {
                this.$root.$bvToast.hide();
            },

            /*
             * Creates a close button element in order to close the toast
             */
            $_createCloseBtn() {
                return this.$createElement('button', {
                    class: ['close'],
                    domProps: { innerHTML: '&times;' },
                    on: { click: this.$_clickHandler }
                });
            },

            /*
             * Creates a p element needed for the message in toaster body
             * @param message
             */
            $_createMessage(message) {
                return this.$createElement('p', {
                    domProps: { innerHTML: message }
                });
            }
            /*** Private functions for toast component ***/
        }
    };
</script>
